import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { useAtomValue } from 'jotai';
import { Link, PlainLink } from 'components/link';
import { provisionsAtom } from 'components/provisioned-document';
import type {
  CitationRegulatedTargetResponse,
  DocumentResponse,
  SalesforceDivisionOrderLineItemLink,
} from 'types/api-responses';
import { EMPTY_CELL } from 'utils/empty-cell';
import { documentProvisionStore } from '../common/atoms';
import style from './division-order-lines-table.module.scss';

/**
 * A provision value as cell text: strings and numbers as written, anything
 * else (missing, or not a scalar) as empty.
 * @param {unknown} value - A value from the raw provision JSON
 * @return {string} The text
 */
const scalarText = (value: unknown): string =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : '';

/**
 * A provision value as table cell content: its text, or the empty indicator.
 * @param {unknown} value - A value from the raw provision JSON
 * @return {string} The cell text
 */
const cellText = (value: unknown): string => scalarText(value) || EMPTY_CELL;

/**
 * Row keys for the provision's owner lines: the well and owner identifiers,
 * suffixed by how many earlier lines share them, since the payload carries no
 * row id and one owner can appear on several lines.
 * @param {Record<string, unknown>[]} lines - The division order's owner lines
 * @return {string[]} One unique key per line, in order
 */
const lineKeys = (lines: Record<string, unknown>[]): string[] => {
  const seen = new Map<string, number>();
  return lines.map((line) => {
    const base = `${scalarText(line.api_number ?? line.uwi)}-${scalarText(
      line.owner_number
    )}`;
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return `${base}-${count}`;
  });
};

// TODO: DivisionOrderLinesTable reads raw LLM provision JSON rather than
// persisted citations. This is intentional for now because DO lines are
// many-owners-per-well (e.g. 3 owners × 1 well = 3 rows), but
// DivisionOrderTarget has a unique constraint on (document_id, target_type,
// target_id), so it can only store one record per well. Adding a `data jsonb`
// column (like AfeTarget) would silently drop all but the last owner per well.
// The proper fix is to remove the unique constraint and treat DivisionOrderTarget
// as a line-item record (one per owner row), then drive this table off
// document.division_order_targets[*].data instead of the provision payload.
/**
 * The division order's owner lines from its latest provision, each with the
 * well its property number was matched to and its Salesforce line item.
 * @param {{ document: DocumentResponse }} props - The division order document
 * @return {JSX.Element | null} The lines table, or null for another document type or no lines
 */
export function DivisionOrderLinesTable({
  document,
}: {
  document: DocumentResponse;
}) {
  const provisions = useAtomValue(provisionsAtom, {
    store: documentProvisionStore,
  });

  if (document.type !== 'Document::DivisionOrder') return null;
  if (provisions.state !== 'hasData') return null;
  if (!provisions.data || 'error' in provisions.data) return null;

  const latestProvision = provisions.data[0];
  if (!latestProvision?.content) return null;

  const content = latestProvision.content as Record<string, unknown>;
  const divisionOrders = content.division_orders as Record<string, unknown>[];
  if (!divisionOrders?.length) return null;

  const lines = divisionOrders[0].division_order_lines as Record<
    string,
    unknown
  >[];
  if (!lines?.length) return null;

  const sfLineItemByLineIndex: Record<
    number,
    SalesforceDivisionOrderLineItemLink
  > = {};
  const rawLinks = document.salesforceDivisionOrderLineItems;
  if (Array.isArray(rawLinks)) {
    rawLinks.forEach((link) => {
      if (
        link &&
        typeof link === 'object' &&
        link.doIndex === 0 &&
        typeof link.lineIndex === 'number'
      ) {
        sfLineItemByLineIndex[link.lineIndex] = link;
      }
    });
  }

  // The LLM payload often leaves `api_number` / `uwi` blank per line; the
  // actual well match for the document is carried by `division_order_target`
  // citations (provision_value = property_number, citable = Well). Build a
  // property_number → matched well lookup so the row can render the resolved
  // well's UWI / name with a link.
  const wellByPropertyNumber: Record<string, CitationRegulatedTargetResponse> =
    {};
  (document.citations ?? []).forEach((citation) => {
    if (citation.provisionField !== 'division_order_target') return;
    const well = citation.regulatedTargets?.find((t) => t.type === 'Well');
    if (well && citation.provisionValue) {
      wellByPropertyNumber[String(citation.provisionValue)] = well;
    }
  });

  const keys = lineKeys(lines);

  return (
    <div className={style.doLinesContainer}>
      <TableContainer title="Division Order Information">
        <Table size="lg">
          <TableHead>
            <TableRow>
              <TableHeader>Well (API/UWI)</TableHeader>
              <TableHeader>Well Name</TableHeader>
              <TableHeader>Division Order</TableHeader>
              <TableHeader>Property No.</TableHeader>
              <TableHeader>Product</TableHeader>
              <TableHeader>Operator</TableHeader>
              <TableHeader>Owner Number</TableHeader>
              <TableHeader>Owner</TableHeader>
              <TableHeader>Interest Type</TableHeader>
              <TableHeader>Interest Percentage</TableHeader>
              <TableHeader>Property Description</TableHeader>
              <TableHeader>SF Line Item</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, idx) => {
              const sfLink = sfLineItemByLineIndex[idx];
              const propertyNumber = scalarText(line.property_number);
              const matchedWell = propertyNumber
                ? wellByPropertyNumber[propertyNumber]
                : undefined;
              const wellIdentifier =
                matchedWell?.uwi ||
                scalarText(line.api_number) ||
                scalarText(line.uwi);
              return (
                <TableRow key={keys[idx]}>
                  <TableCell>
                    {matchedWell ? (
                      <Link to={matchedWell.href}>
                        {wellIdentifier || matchedWell.displayName}
                      </Link>
                    ) : (
                      wellIdentifier || EMPTY_CELL
                    )}
                  </TableCell>
                  <TableCell>
                    {matchedWell?.displayName ?? cellText(line.property_name)}
                  </TableCell>
                  <TableCell>{cellText(line.division_order_number)}</TableCell>
                  <TableCell>{cellText(line.property_number)}</TableCell>
                  <TableCell>{cellText(line.product)}</TableCell>
                  <TableCell>{cellText(line.operator)}</TableCell>
                  <TableCell>{cellText(line.owner_number)}</TableCell>
                  <TableCell>{cellText(line.owner)}</TableCell>
                  <TableCell>{cellText(line.interest_type)}</TableCell>
                  <TableCell>{cellText(line.interest_percentage)}</TableCell>
                  <TableCell>{cellText(line.property_description)}</TableCell>
                  <TableCell>
                    {sfLink?.href ? (
                      <PlainLink
                        href={sfLink.href}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {sfLink.sfLineItemName ??
                          sfLink.sfLineItemId ??
                          EMPTY_CELL}
                      </PlainLink>
                    ) : (
                      EMPTY_CELL
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
